// ============================================================
// TargetCharacteristicsTab - the Target "Overview" tab.
// Character-spawned targets: WoundDashboard + the harmonized
// OverviewSheet (identical structure to the attacker sheet).
// Plain targets keep the reduced mode: WoundDashboard, derived
// tests, basic stats, combat rules, dice tray.
// ============================================================
import { memo, useMemo, useState } from 'react';
import type { DiceResult, Target } from '../types';
import type { Character, CharacteristicId } from '../engine/character/types';
import { deriveCombatRules } from '../engine/combatRules';
import { healthBandLabel, healthColor } from '../engine/healthRamp';
import { useWoundDisplay } from '../hooks/useWoundDisplay';
import { DEFAULT_DIFFICULTY_ID, getDifficulty } from '../engine/characteristicTests';
import type { PlainTargetTestId, SkillTestDef } from '../engine/characteristicTests';
import { getConditionTestModifier, describeConditionModifiers } from '../engine/conditionModifiers';
import DifficultySelector from './DifficultySelector';
import OverviewSheet from './overview/OverviewSheet';
import PlainTargetTestRow from './PlainTargetTestRow';
import DiceTray from './DiceTray';
import common from '../styles/common.module.css';
import styles from '../styles/TargetPanel.module.css';
import type { ConditionModOptions } from '../engine/conditionModifiers';

interface Props {
  target: Target;
  sourceCharacter: Character | null;
  onRollCharacteristic: (
    charId: CharacteristicId,
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => void;
  onRollSkill: (
    skillId: SkillTestDef['id'],
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => void;
  onRollPlainTarget: (
    testId: PlainTargetTestId,
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => void;
  /** Right-click opposed rolls vs the acting character (v8.220.0). */
  onOpposedCharacteristic?: ((charId: CharacteristicId) => boolean) | null;
  onOpposedSkill?: ((skillId: SkillTestDef['id']) => boolean) | null;
  onRollDiceExpr: (expr: string) => DiceResult | null;
  /** Transient ×N badge overrides (sheet rolls only). */
  unnaturalOverrides?: Partial<Record<CharacteristicId, number>>;
  onUnnaturalOverride?: (charId: CharacteristicId, multiplier: number | null) => void;
  /** GM house rule: fold the target's active conditions into every test. */
  applyConditionModifiers?: boolean;
  /** Which condition modifiers the table has switched off. */
  condModOptions?: ConditionModOptions;
  /** Tests a condition forbids, keyed by id; the value is the reason. */
  blockedTests?: Record<string, string>;
}

/** Domain lookup for the plain-target derived tests. */
const PLAIN_TEST_DOMAIN: Record<PlainTargetTestId, { characteristic: CharacteristicId; skill?: string }> = {
  dodge: { characteristic: 'agi', skill: 'dodge' },
  parry: { characteristic: 'ws', skill: 'parry' },
  toughness: { characteristic: 'tgh' },
  strength: { characteristic: 'str' },
  willpower: { characteristic: 'wp' },
};

/** The plain axes name the characteristic they stand in for (for an opposed roll). */
const AXIS_CHAR: Record<PlainTargetTestId, CharacteristicId> = {
  dodge: 'agi',
  parry: 'ws',
  toughness: 'tgh',
  strength: 'str',
  willpower: 'wp',
};

/** A condition modifier as a roll carries it: the labels joined, and the total. */
type ExtraMod = { label: string; value: number };

/** Each plain test's condition modifier (defender house rule), for the rolls and the row. */
function plainConditionExtras(
  target: Target,
  apply: boolean,
  options: ConditionModOptions | undefined,
): Partial<Record<PlainTargetTestId, ExtraMod>> {
  if (!apply) return {};
  const out: Partial<Record<PlainTargetTestId, ExtraMod>> = {};
  for (const id of Object.keys(PLAIN_TEST_DOMAIN) as PlainTargetTestId[]) {
    const { total, labels } = getConditionTestModifier(target, PLAIN_TEST_DOMAIN[id], options);
    if (total !== 0) out[id] = { label: labels.join(' · '), value: total };
  }
  return out;
}

// ────────────────────────────────────────────────────────────
// Wound Dashboard Component
// ────────────────────────────────────────────────────────────

function WoundDashboard({ target }: { target: Target }) {
  const { current, max } = target.wounds;
  const { showNumbers } = useWoundDisplay();
  const pct = max > 0 ? (current / max) * 100 : 0;
  // A FIFTH COPY OF THE HEALTH RAMP lived here, with its own 75/25
  // thresholds and its own raw hex - so this card said amber where the
  // roster chip beside it said green. `healthRamp.ts` exists precisely to
  // stop that, and its header says as much.
  const barColor = healthColor(current, max);
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>WOUNDS</div>
      <div className={styles.healthBar}>
        <div className={styles.healthBarFill} style={{ width: Math.max(0, pct) + '%', background: barColor }} />
      </div>
      <div className={styles.hpText}>
        {/* The percentage is the number in another costume, and the
            severity IS the wound count past zero - both go with it. */}
        {showNumbers ? `${current}/${max} wounds (${Math.max(0, Math.round(pct))}%)` : healthBandLabel(current, max)}
      </div>
      <WoundWarning current={current} showNumbers={showNumbers} />
      <CriticalDamageList crits={target.criticalDamage} />
    </div>
  );
}

/** CRITICAL (with its severity when numbers show) below zero; a warning at zero. */
function WoundWarning({ current, showNumbers }: { current: number; showNumbers: boolean }) {
  if (current < 0) {
    return (
      <div style={{ textAlign: 'center', marginTop: 2 }}>
        <span style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 'bold' }}>
          {showNumbers ? `CRITICAL (Severity ${Math.abs(current)})` : 'CRITICAL'}
        </span>
      </div>
    );
  }
  if (current !== 0 || !showNumbers) return null;
  return (
    <div style={{ textAlign: 'center', marginTop: 2 }}>
      <span style={{ fontSize: 10, color: 'var(--warn)', fontWeight: 'bold' }}>At 0 - next hit is Critical</span>
    </div>
  );
}

function CriticalDamageList({ crits }: { crits: Target['criticalDamage'] }) {
  if (!crits || crits.length === 0) return null;
  return (
    <div className={styles.critList}>
      <div className={styles.critTitle}>Critical Damage</div>
      {crits.map((crit, idx) => (
        <div key={idx} className={styles.critEntry}>
          {crit}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────

function TargetCharacteristicsTab(props: Props) {
  const { target, sourceCharacter } = props;
  // The test difficulty lives here, not in the plain view, so it survives
  // the target gaining (and losing) a character sheet.
  const [difficultyId, setDifficultyId] = useState(DEFAULT_DIFFICULTY_ID);
  const [customMod, setCustomMod] = useState(0);
  if (!sourceCharacter) {
    const difficulty = { difficultyId, setDifficultyId, customMod, setCustomMod };
    return <PlainTargetOverview {...props} difficulty={difficulty} />;
  }
  // Character-spawned target - the harmonized sheet (identical to attacker).
  return (
    <div style={{ padding: '8px 0', overflow: 'auto' }}>
      {/* Wound dashboard stays defender-only, above the shared sections */}
      <WoundDashboard target={target} />
      <OverviewSheet
        character={sourceCharacter}
        target={target}
        onRollCharacteristic={props.onRollCharacteristic}
        onRollSkill={props.onRollSkill}
        onRollDiceExpr={props.onRollDiceExpr}
        onOpposedCharacteristic={props.onOpposedCharacteristic ?? null}
        onOpposedSkill={props.onOpposedSkill ?? null}
        unnaturalOverrides={props.unnaturalOverrides}
        onUnnaturalOverride={props.onUnnaturalOverride}
        applyConditionModifiers={props.applyConditionModifiers ?? false}
        condModOptions={props.condModOptions}
        blockedTests={props.blockedTests}
      />
    </div>
  );
}

interface Difficulty {
  difficultyId: string;
  setDifficultyId: (id: string) => void;
  customMod: number;
  setCustomMod: (mod: number) => void;
}

/** A plain (template-loaded) target - the reduced mode: wounds, derived tests, basic stats, rules, dice tray. */
function PlainTargetOverview(props: Props & { difficulty: Difficulty }) {
  const { target } = props;
  const rules = useMemo(() => deriveCombatRules(target), [target]);
  return (
    <div style={{ padding: '8px 0', overflow: 'auto' }}>
      {/* Wound dashboard always shows */}
      <WoundDashboard target={target} />
      <PlainTestsCard {...props} />
      {/* Basic stats for template-loaded targets */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>BASIC STATS</div>
        <div className={common.overviewGrid} style={{ padding: 0 }}>
          <div className={common.overviewItem}>
            <span>TB</span>
            <span className={common.overviewValue}>
              {target.toughnessBonus}
              {target.unnaturalToughness > 1 && <span style={{ fontSize: 9 }}> ×{target.unnaturalToughness}</span>}
            </span>
          </div>
          <div className={common.overviewItem}>
            <span>Size</span>
            <span className={common.overviewValue}>{target.size}</span>
          </div>
        </div>
      </div>
      <CombatRulesCard rules={rules} />
      <div className={styles.card}>
        <div className={styles.cardTitle}>DICE TRAY</div>
        <DiceTray onRollExpr={props.onRollDiceExpr} />
      </div>
    </div>
  );
}

/** The derived deliberate tests, with the condition modifiers each roll carries when the house rule is on. */
function PlainTestsCard({
  target,
  difficulty,
  blockedTests,
  onRollPlainTarget,
  ...props
}: Props & { difficulty: Difficulty }) {
  const apply = props.applyConditionModifiers ?? false;
  const { condModOptions } = props;
  const extras = useMemo(() => plainConditionExtras(target, apply, condModOptions), [apply, target, condModOptions]);
  const summary = useMemo(
    () => (apply ? describeConditionModifiers(target, condModOptions) : []),
    [apply, target, condModOptions],
  );
  /** The same values the rolls carry, as a map the row can SHOW. */
  const conditionMods = useMemo(
    () => Object.fromEntries(Object.entries(extras).map(([id, e]) => [id, e!.value])),
    [extras],
  );
  const difficultyMod = getDifficulty(difficulty.difficultyId)?.mod ?? 0;
  const opposed = props.onOpposedCharacteristic;
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>TESTS</div>
      <DifficultySelector
        difficultyId={difficulty.difficultyId}
        customMod={difficulty.customMod}
        onDifficultyChange={difficulty.setDifficultyId}
        onCustomModChange={difficulty.setCustomMod}
      />
      <PlainTargetTestRow
        blocked={blockedTests}
        conditionMods={conditionMods}
        target={target}
        onRoll={(testId) => onRollPlainTarget(testId, difficultyMod, difficulty.customMod, extras[testId])}
        onOpposed={opposed ? (testId) => opposed(AXIS_CHAR[testId]) : null}
      />
      {apply && summary.length > 0 && (
        <ul className={styles.conditionList} data-testid="condition-modifiers-list">
          {summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CombatRulesCard({ rules }: { rules: ReturnType<typeof deriveCombatRules> }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>COMBAT RULES</div>
      {rules.length > 0 ? (
        <div className={styles.rulesList}>
          {rules.map((r, i) => (
            <div key={i} className={styles.ruleEntry}>
              <span className={styles.ruleName}>{r.name}</span>
              <span className={styles.ruleDesc}>{r.description}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyRules}>No special combat rules.</div>
      )}
    </div>
  );
}

export default memo(TargetCharacteristicsTab);
